import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon,
  AttachMoney,
  Assignment,
  TrendingUp,
  CheckCircle,
  Cancel,
  Schedule,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import api from '../api/client';
import CompactMetricCard from '../components/common/CompactMetricCard';

interface PORecord {
  id: number;
  po_id: string;
  po_title: string;
  rev_type?: string;
  rev_class?: string;
  am?: string;
  pm?: string;
  geo?: string;
  account?: string;
  project?: string;
  project_type?: string;
  milestone_desc?: string;
  po_date: string;
  po_value?: number;
  currency?: string;
  exchange_rate?: number;
  po_value_usd?: number;
  payment_terms?: string;
  status: string;
  referenced_msa?: string;
  remarks?: string;
  created_at: string;
  updated_at?: string;
}

interface POSummary {
  total_pos: number;
  active_pos: number;
  expired_pos: number;
  cancelled_pos: number;
  partially_cancelled_pos: number;
  total_value_usd: number;
  active_value_usd: number;
  expired_value_usd: number;
  cancelled_value_usd: number;
  avg_po_value: number;
  largest_po: {
    value: number;
    account: string;
    project: string;
  };
  most_recent_po: {
    date: string;
    account: string;
    project: string;
  };
}

interface POAnalysisPageProps {
  startDate?: string | null;
  endDate?: string | null;
}

const POAnalysisPage: React.FC<POAnalysisPageProps> = ({ startDate, endDate }) => {
  const [pos, setPOs] = useState<PORecord[]>([]);
  const [filteredPOs, setFilteredPOs] = useState<PORecord[]>([]);
  const [summary, setSummary] = useState<POSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPO, setEditingPO] = useState<PORecord | null>(null);
  const [formData, setFormData] = useState<Partial<PORecord>>({});
  
  // Filter and pagination state
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [accountFilter, setAccountFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedPanels, setExpandedPanels] = useState<Set<number>>(new Set());
  const itemsPerPage = 20;

  // Fetch POs and summary
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params for date filtering
      const params = new URLSearchParams();
      if (startDate) {
        // Convert YYYY-MM to YYYY-MM-01
        params.append('start_date', `${startDate}-01`);
      }
      if (endDate) {
        // Convert YYYY-MM to last day of month
        const endDateObj = new Date(`${endDate}-01`);
        const lastDay = new Date(endDateObj.getFullYear(), endDateObj.getMonth() + 1, 0);
        params.append('end_date', lastDay.toISOString().split('T')[0]);
      }
      
      const queryString = params.toString();
      const apiSuffix = queryString ? `?${queryString}` : '';
      
      const [posResponse, summaryResponse] = await Promise.all([
        api.get(`/api/v1/delivery/po-master${apiSuffix}`),
        api.get(`/api/v1/delivery/po-master/summary/statistics${apiSuffix}`),
      ]);
      
      // Sort by most recent PO date first using string comparison (descending)
      const sortedPOs = (posResponse.data.pos || []).sort((a: PORecord, b: PORecord) => {
        const aDate = a.po_date || '';
        const bDate = b.po_date || '';
        return bDate.localeCompare(aDate);
      });
      
      setPOs(sortedPOs);
      setFilteredPOs(sortedPOs);
      setSummary(summaryResponse.data.summary || null);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...pos];
    
    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(po => po.status === statusFilter);
    }
    
    // Account filter
    if (accountFilter) {
      filtered = filtered.filter(po => 
        po.account?.toLowerCase().includes(accountFilter.toLowerCase())
      );
    }
    
    // Project filter
    if (projectFilter) {
      filtered = filtered.filter(po => 
        po.project?.toLowerCase().includes(projectFilter.toLowerCase())
      );
    }
    
    setFilteredPOs(filtered);
    setPage(1); // Reset to first page when filters change
  }, [pos, statusFilter, accountFilter, projectFilter]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const handleOpenDialog = (po?: PORecord) => {
    if (po) {
      setEditingPO(po);
      setFormData(po);
    } else {
      setEditingPO(null);
      setFormData({
        status: 'Active',
        currency: 'USD',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPO(null);
    setFormData({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (editingPO) {
        // Update existing PO
        await api.put(`/api/v1/delivery/po-master/${editingPO.id}`, formData);
      } else {
        // Create new PO
        await api.post('/api/v1/delivery/po-master', formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      alert('Error saving PO. Please check the //console for details.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this PO?')) {
      try {
        await api.delete(`/api/v1/delivery/po-master/${id}`);
        fetchData();
      } catch (error) {
      }
    }
  };

  const handleAccordionChange = (poId: number) => {
    const newExpanded = new Set(expandedPanels);
    if (newExpanded.has(poId)) {
      newExpanded.delete(poId);
    } else {
      newExpanded.add(poId);
    }
    setExpandedPanels(newExpanded);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setStatusFilter('All');
    setAccountFilter('');
    setProjectFilter('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Expired':
        return 'default';
      case 'Cancelled':
        return 'error';
      case 'Partially Cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              PO Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Purchase Order and SOW Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              variant="contained"
            >
              Add PO
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <CompactMetricCard
            title="Total POs"
            value={summary?.total_pos || 0}
            icon={Assignment}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <CompactMetricCard
            title="Total Value"
            value={formatCurrency(summary?.total_value_usd)}
            icon={AttachMoney}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <CompactMetricCard
            title="Active POs"
            value={summary?.active_pos || 0}
            subtitle={`Value: ${formatCurrency(summary?.active_value_usd)}`}
            icon={CheckCircle}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <CompactMetricCard
            title="Expired POs"
            value={summary?.expired_pos || 0}
            subtitle={`Value: ${formatCurrency(summary?.expired_value_usd)}`}
            icon={Schedule}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <CompactMetricCard
            title="Cancelled POs"
            value={summary?.cancelled_pos || 0}
            subtitle={`Value: ${formatCurrency(summary?.cancelled_value_usd)}`}
            icon={Cancel}
            color="error"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Average PO Value"
            value={formatCurrency(summary?.avg_po_value)}
            icon={TrendingUp}
            color="secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Largest PO"
            value={formatCurrency(summary?.largest_po?.value)}
            subtitle={`${summary?.largest_po?.account || 'N/A'} - ${summary?.largest_po?.project || 'N/A'}`}
            icon={AttachMoney}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CompactMetricCard
            title="Most Recent PO"
            value={summary?.most_recent_po?.date ? formatDate(summary.most_recent_po.date) : 'N/A'}
            subtitle={`${summary?.most_recent_po?.account || 'N/A'} - ${summary?.most_recent_po?.project || 'N/A'}`}
            icon={Schedule}
            color="info"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Expired">Expired</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="Partially Cancelled">Partially Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Account"
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              placeholder="Type to search..."
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Project"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              placeholder="Type to search..."
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button 
              variant="outlined" 
              onClick={handleClearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, filteredPOs.length)} of {filteredPOs.length} POs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {expandedPanels.size} expanded
          </Typography>
        </Box>
      </Paper>

      {/* PO Accordion List */}
      <Box sx={{ mb: 2 }}>
        {filteredPOs
          .slice((page - 1) * itemsPerPage, page * itemsPerPage)
          .map((po) => (
            <Accordion
              key={po.id}
              expanded={expandedPanels.has(po.id)}
              onChange={() => handleAccordionChange(po.id)}
              sx={{
                mb: 1,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0',
                    alignItems: 'center',
                  },
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {po.po_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(po.po_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" noWrap>
                      {po.po_title}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="subtitle2" color="primary">
                      {formatCurrency(po.po_value_usd)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Chip
                      label={po.status}
                      color={getStatusColor(po.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography variant="caption" color="text.secondary" align="right">
                      {po.account || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, borderTop: '1px solid #e0e0e0' }}>
                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        PROJECT DETAILS
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Account:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.account || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Project:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.project || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Geography:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.geo || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        CLASSIFICATION
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Rev Type:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.rev_type || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Rev Class:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.rev_class || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Project Type:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.project_type || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        TEAM
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Account Manager:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.am || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Project Manager:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.pm || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        FINANCIAL DETAILS
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">PO Value:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {po.po_value ? `${po.currency || ''} ${po.po_value.toLocaleString()}` : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Exchange Rate:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.exchange_rate || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">PO Value (USD):</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatCurrency(po.po_value_usd)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Payment Terms:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.payment_terms || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        ADDITIONAL INFO
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Referenced MSA:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">{po.referenced_msa || '-'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Status:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Chip
                            label={po.status}
                            color={getStatusColor(po.status)}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {po.milestone_desc && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          MILESTONE DESCRIPTION
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {po.milestone_desc}
                        </Typography>
                      </Box>
                    )}

                    {po.remarks && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          REMARKS
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {po.remarks}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        
        {filteredPOs.length === 0 && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No PO records found</Typography>
          </Paper>
        )}
      </Box>

      {/* Pagination */}
      {filteredPOs.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredPOs.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingPO ? 'Edit PO' : 'Add New PO'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PO ID / SOW ID"
                fullWidth
                value={formData.po_id || ''}
                onChange={(e) => handleInputChange('po_id', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PO Date"
                type="date"
                fullWidth
                value={formData.po_date || ''}
                onChange={(e) => handleInputChange('po_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="PO Title"
                fullWidth
                value={formData.po_title || ''}
                onChange={(e) => handleInputChange('po_title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account"
                fullWidth
                value={formData.account || ''}
                onChange={(e) => handleInputChange('account', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project"
                fullWidth
                value={formData.project || ''}
                onChange={(e) => handleInputChange('project', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Status"
                select
                fullWidth
                value={formData.status || 'Active'}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Expired">Expired</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="Partially Cancelled">Partially Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project Type"
                select
                fullWidth
                value={formData.project_type || ''}
                onChange={(e) => handleInputChange('project_type', e.target.value)}
              >
                <MenuItem value="Staff Aug">Staff Aug</MenuItem>
                <MenuItem value="Managed Services">Managed Services</MenuItem>
                <MenuItem value="Turn key">Turn key</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Rev Type"
                select
                fullWidth
                value={formData.rev_type || ''}
                onChange={(e) => handleInputChange('rev_type', e.target.value)}
              >
                <MenuItem value="ENEB">ENEB</MenuItem>
                <MenuItem value="ENNB">ENNB</MenuItem>
                <MenuItem value="NNNB">NNNB</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Rev Class"
                select
                fullWidth
                value={formData.rev_class || ''}
                onChange={(e) => handleInputChange('rev_class', e.target.value)}
              >
                <MenuItem value="Backlog">Backlog</MenuItem>
                <MenuItem value="New">New</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="PO Value"
                type="number"
                fullWidth
                value={formData.po_value || ''}
                onChange={(e) => handleInputChange('po_value', parseFloat(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Currency"
                select
                fullWidth
                value={formData.currency || 'USD'}
                onChange={(e) => handleInputChange('currency', e.target.value)}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Exchange Rate"
                type="number"
                fullWidth
                value={formData.exchange_rate || ''}
                onChange={(e) => handleInputChange('exchange_rate', parseFloat(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PO Value (USD)"
                type="number"
                fullWidth
                value={formData.po_value_usd || ''}
                onChange={(e) => handleInputChange('po_value_usd', parseFloat(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Terms"
                fullWidth
                value={formData.payment_terms || ''}
                onChange={(e) => handleInputChange('payment_terms', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account Manager (AM)"
                fullWidth
                value={formData.am || ''}
                onChange={(e) => handleInputChange('am', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project Manager (PM)"
                fullWidth
                value={formData.pm || ''}
                onChange={(e) => handleInputChange('pm', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Geography"
                fullWidth
                value={formData.geo || ''}
                onChange={(e) => handleInputChange('geo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Referenced MSA"
                fullWidth
                value={formData.referenced_msa || ''}
                onChange={(e) => handleInputChange('referenced_msa', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Milestone Description"
                fullWidth
                multiline
                rows={2}
                value={formData.milestone_desc || ''}
                onChange={(e) => handleInputChange('milestone_desc', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                fullWidth
                multiline
                rows={3}
                value={formData.remarks || ''}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingPO ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POAnalysisPage;

